import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings, X, AlertTriangle, Shield, Sun, Moon, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useDemoMode, DatasetModeControl } from '@/components/common/DemoModeToggle';
import { SecurityPrivacyPanel } from '@/components/common/SecurityPrivacyPanel';
import { trackEvent } from '@/hooks/useAnalytics';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';

export function SettingsButton() {
  const [searchParams, setSearchParams] = useSearchParams();
  const open = searchParams.get('modal') === 'settings';

  const handleOpen = () => {
    const next = new URLSearchParams(searchParams);
    next.set('modal', 'settings');
    setSearchParams(next, { replace: true });
  };

  const handleClose = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('modal');
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      <button
        onClick={handleOpen}
        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        data-testid="settings-button"
        aria-label="Settings"
      >
        <Settings className="w-4 h-4" />
      </button>

      {open && <SettingsModal onClose={handleClose} />}
    </>
  );
}

function SettingsModal({ onClose }) {
  const { isDemoMode, toggleDemoMode } = useDemoMode();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const isAdmin = user?.role === 'ADMIN';
  const [tab, setTab] = useState('appearance');

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-16" data-testid="settings-modal">
      <div className="fixed inset-0 bg-background/60 backdrop-blur-sm" onClick={onClose} />
      <Card className="relative z-10 w-full max-w-lg bg-card border-border shadow-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border/50">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-muted/50 text-muted-foreground"
            data-testid="settings-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs — Advanced only visible to ADMIN */}
        <div className="flex border-b border-border/30 px-4">
          <button
            onClick={() => setTab('appearance')}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${tab === 'appearance' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid="settings-tab-appearance"
          >
            <Sun className="w-3 h-3 inline mr-1.5" />
            Appearance
          </button>
          <button
            onClick={() => setTab('security')}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${tab === 'security' ? 'border-stable text-stable' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid="settings-tab-security"
          >
            <Shield className="w-3 h-3 inline mr-1.5" />
            Security & Privacy
          </button>
          {isAdmin && (
            <button
              onClick={() => setTab('advanced')}
              className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${tab === 'advanced' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid="settings-tab-advanced"
            >
              <AlertTriangle className="w-3 h-3 inline mr-1.5" />
              Advanced
            </button>
          )}
        </div>

        <CardContent className="p-4 overflow-y-auto flex-1">
          {tab === 'appearance' && (
            <div className="space-y-4" data-testid="settings-appearance">
              <div>
                <p className="text-xs font-medium text-foreground mb-1">Theme</p>
                <p className="text-2xs text-muted-foreground mb-3">Select your preferred appearance.</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'light', label: 'Light', Icon: Sun },
                    { id: 'dark', label: 'Dark', Icon: Moon },
                    { id: 'system', label: 'System', Icon: Monitor },
                  ].map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      onClick={() => { setTheme(id); trackEvent('theme_change', { theme: id }); }}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 text-xs font-medium transition-all ${
                        theme === id
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border/50 text-muted-foreground hover:border-border hover:text-foreground'
                      }`}
                      data-testid={`theme-${id}`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'security' && <SecurityPrivacyPanel />}

          {tab === 'advanced' && isAdmin && (
            <div className="space-y-5">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className="text-2xs border-border/50 text-muted-foreground">
                    Advanced
                  </Badge>
                  <div className="flex items-center gap-1 text-2xs text-warning/70">
                    <AlertTriangle className="w-3 h-3" />
                    Internal only
                  </div>
                </div>
                <DatasetModeControl isDemoMode={isDemoMode} onToggle={() => { toggleDemoMode(); trackEvent('settings_change_dataset_mode', { demo_mode: !isDemoMode }); }} />
              </div>

              <div className="pt-3 border-t border-border/30">
                <p className="text-2xs text-muted-foreground/50">
                  YBV Platform v2.0 — Changes persist across sessions.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SettingsButton;
